import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACCOUNT_API_ENDPOINT } from './config';
import {
  AuthError,
  beginTotpSetup,
  changePassword,
  confirmTotpSetup,
  confirmTotpSignIn,
  confirmForgotPassword,
  confirmSignUp,
  exchangeCodeForTokens,
  forgotPassword,
  hostedUiSignOutUrl,
  refreshTokens,
  resendConfirmationCode,
  revokeRefreshToken,
  signIn,
  signUp,
  startGoogleSignIn,
  totpEnabled,
} from './cognito';
import {
  IdTokenClaims,
  StoredTokens,
  clearTokens,
  decodeIdToken,
  isExpired,
  loadTokens,
  saveTokens,
} from './tokenStore';
import { takeFlow } from './pkce';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  isFederated: boolean;
}

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  confirmTotpSignIn: (code: string, rememberDevice: boolean) => Promise<boolean>;
  signUp: (email: string, password: string, name: string) => Promise<{ userConfirmed: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, password: string) => Promise<void>;
  signInWithGoogle: (returnTo: string) => Promise<void>;
  completeGoogleSignIn: (code: string, state: string) => Promise<string>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  totpEnabled: () => Promise<boolean>;
  beginTotpSetup: () => Promise<string>;
  confirmTotpSetup: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  authFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const toUser = (claims: IdTokenClaims, provider?: string): AuthUser => ({
  userId: claims.sub,
  email: claims.email,
  name: claims.name || '',
  isFederated: provider ? provider !== 'email' : Boolean(claims.identities),
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const tokens = useRef<StoredTokens | null>(null);

  const applyTokens = useCallback((next: StoredTokens): IdTokenClaims => {
    const claims = decodeIdToken(next.idToken);
    if (!claims) {
      throw new AuthError('InvalidToken', 'The sign-in session could not be read.');
    }

    tokens.current = next;
    saveTokens(next);
    setUser(toUser(claims, next.provider));
    setStatus('signedIn');
    return claims;
  }, []);

  const forgetSession = useCallback(() => {
    tokens.current = null;
    clearTokens();
    setUser(null);
    setStatus('signedOut');
  }, []);

  const validTokens = useCallback(async (): Promise<StoredTokens | null> => {
    const current = tokens.current;
    if (!current) {
      return null;
    }

    const claims = decodeIdToken(current.idToken);
    if (!claims) {
      forgetSession();
      return null;
    }
    if (!isExpired(claims)) {
      return current;
    }

    try {
      const refreshed = {
        ...(await refreshTokens(claims['cognito:username'], current.refreshToken)),
        provider: current.provider,
      };
      applyTokens(refreshed);
      return refreshed;
    } catch {
      forgetSession();
      return null;
    }
  }, [applyTokens, forgetSession]);

  useEffect(() => {
    const stored = loadTokens();
    if (!stored) {
      setStatus('signedOut');
      return;
    }

    const claims = decodeIdToken(stored.idToken);
    if (!claims) {
      clearTokens();
      setStatus('signedOut');
      return;
    }

    if (!isExpired(claims)) {
      tokens.current = stored;
      setUser(toUser(claims, stored.provider));
      setStatus('signedIn');
      return;
    }

    refreshTokens(claims['cognito:username'], stored.refreshToken)
      .then((refreshed) => ({ ...refreshed, provider: stored.provider }))
      .then((refreshed) => applyTokens(refreshed))
      .catch(() => {
        clearTokens();
        setStatus('signedOut');
      });
  }, [applyTokens]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      signIn: async (email, password) => {
        applyTokens({ ...(await signIn(email, password)), provider: 'email' });
      },
      confirmTotpSignIn: async (code, rememberDevice) => {
        const result = await confirmTotpSignIn(code, rememberDevice);
        applyTokens({ ...result.tokens, provider: 'email' });
        return result.deviceRemembered;
      },
      signUp: (email, password, name) => signUp(email, password, name),
      confirmSignUp: (email, code) => confirmSignUp(email, code),
      resendConfirmationCode: (email) => resendConfirmationCode(email),
      forgotPassword: (email) => forgotPassword(email),
      confirmForgotPassword: (email, code, password) =>
        confirmForgotPassword(email, code, password),
      signInWithGoogle: (returnTo) => startGoogleSignIn(returnTo),
      completeGoogleSignIn: async (code, state) => {
        const flow = takeFlow();
        if (!flow.verifier || !flow.state || flow.state !== state) {
          throw new AuthError('StateMismatch', 'This sign-in link is no longer valid. Please try again.');
        }
        applyTokens({ ...(await exchangeCodeForTokens(code, flow.verifier)), provider: 'google' });
        return flow.returnTo;
      },
      changePassword: async (currentPassword, newPassword) => {
        const current = await validTokens();
        if (!current) {
          throw new AuthError('NotSignedIn', 'Your session has expired. Please sign in again.');
        }
        await changePassword(current.accessToken, currentPassword, newPassword);
      },
      totpEnabled: async () => {
        const current = await validTokens();
        if (!current) throw new AuthError('NotSignedIn', 'Your session has expired. Please sign in again.');
        return totpEnabled(current);
      },
      beginTotpSetup: async () => {
        const current = await validTokens();
        if (!current) throw new AuthError('NotSignedIn', 'Your session has expired. Please sign in again.');
        return beginTotpSetup(current);
      },
      confirmTotpSetup: async (code) => {
        const current = await validTokens();
        if (!current) throw new AuthError('NotSignedIn', 'Your session has expired. Please sign in again.');
        await confirmTotpSetup(current, code);
      },
      signOut: async () => {
        const current = tokens.current;
        const federated = user?.isFederated === true;
        if (current) {
          await revokeRefreshToken(current.refreshToken);
        }
        const logoutUrl = federated ? hostedUiSignOutUrl() : null;
        if (logoutUrl) {
          // Do not update React auth state before leaving for Cognito. Doing so
          // makes RequireAuth render /login once, then Cognito redirects to it
          // a second time. Clear storage silently and replace this history entry.
          tokens.current = null;
          clearTokens();
          window.location.replace(logoutUrl);
          return;
        }
        forgetSession();
      },
      authFetch: async (path, init) => {
        const current = await validTokens();
        if (!current) {
          throw new AuthError('NotSignedIn', 'Your session has expired. Please sign in again.');
        }

        const headers = new Headers(init?.headers);
        headers.set('Authorization', current.idToken);
        if (init?.body && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }

        // The local community preview uses the same verified Cognito account.
        // Its development middleware checks the JWT signature and owner allowlist;
        // production always uses the API Gateway authorizer and private Lambda.
        const endpoint = process.env.NODE_ENV === 'development' && path === '/community/businesses'
          ? '/__community-preview/businesses'
          : `${ACCOUNT_API_ENDPOINT}${path}`;
        return fetch(endpoint, { ...init, headers });
      },
    }),
    [applyTokens, forgetSession, status, user, validTokens]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
